import { ReactNode, useLayoutEffect, useRef, useState } from "react";

export interface OverflowTextProps {
  content: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onOverflowChange?: (isOverflowing: boolean) => void;
  recalcKey?: string | number;
}

const toPlainText = (node: ReactNode): string | null => {
  if (typeof node === "string") return node;
  if (typeof node === "number" || typeof node === "bigint") {
    return String(node);
  }
  return null;
};

export const OverflowText = ({
  content,
  className,
  style,
  onOverflowChange,
  recalcKey,
}: OverflowTextProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const plainText = toPlainText(content);
  const [displayText, setDisplayText] = useState(plainText ?? "");
  const overflowRef = useRef(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || plainText === null) return;

    const fits = () => element.scrollHeight <= element.clientHeight;
    const compute = () => {
      element.textContent = plainText;
      if (fits()) return { text: plainText, overflow: false };

      let lowerBound = 0;
      let upperBound = plainText.length;
      while (lowerBound < upperBound) {
        const midpoint = Math.ceil((lowerBound + upperBound) / 2);
        element.textContent = `${plainText.slice(0, midpoint)}…`;
        if (fits()) lowerBound = midpoint;
        else upperBound = midpoint - 1;
      }

      const text = `${plainText.slice(0, lowerBound)}…`;
      return { text, overflow: text !== plainText };
    };

    let animationFrame: number | null = null;
    const apply = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        const result = compute();
        setDisplayText((current) =>
          current === result.text ? current : result.text,
        );
        if (overflowRef.current !== result.overflow) {
          overflowRef.current = result.overflow;
          onOverflowChange?.(result.overflow);
        }
      });
    };

    apply();
    const resizeObserver = new ResizeObserver(apply);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
    };
  }, [onOverflowChange, plainText, recalcKey]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        whiteSpace: "normal",
        wordBreak: "break-word",
        ...style,
      }}
    >
      {plainText === null ? content : displayText}
    </div>
  );
};
